/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */
import { Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import 'bootstrap-daterangepicker/daterangepicker.css';
import objectPath from 'object-path';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';

import { agentUserId } from '../../../recoil/atoms/agentUserId';
import { user } from '../../../recoil/atoms/user';
import { useHtmlClassService } from '../../../_metronic/layout/_core/MetronicLayout';
import {
  getBreadcrumbsAndTitle,
  useSubheader,
} from '../../../_metronic/layout/_core/MetronicSubheader';

import { BreadCrumbs } from './components/BreadCrumbs';
import { DatePicker } from './components/DatePicker';
import { TimeGrouping } from './TimeGrouping';

const useStyles = makeStyles((theme) => ({
  '@global': theme.palette.mode === 'dark' && {
    '.daterangepicker': {
      color: 'white',
      backgroundColor: 'black',
      '& .ranges li:hover': {
        color: 'white',
        backgroundColor: 'grey',
      },
      '& .calendar-table': {
        backgroundColor: 'black',
      },
      '& td.off, & td.off.in-range, & td.off.start-date, & td.off.end-date': {
        backgroundColor: 'black',
      },
      '& td.in-range': {
        backgroundColor: '#33708c',
        color: 'white',
      },
      '& td.available:hover, & th.available:hover': {
        backgroundColor: 'grey',
      },
    },
    subheader: {
      color: 'white',
      backgroundColor: 'black',
    },
  },
  '#kt_subheader': {
    // background: 'transparent'
  },
  subheader: {
    background: '#fff',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'block',
    height: 'auto !important',
    paddingBottom: '0 !important',
    '.dark-mode &': {
      color: 'white',
      backgroundColor: '#121c21',
    },
  },
  pgDesc: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '.dark-mode &': {
      color: 'white',
      backgroundColor: '#121c21',
    },
  },
  pgDescTxt: {
    '& p': {
      marginBottom: '0',
    },
    '.pgdesc-lg &': {
      maxWidth: '800px',
    },
    '.pgdesc-xlg &': {
      maxWidth: '800px',
    },
  },
}));

export function SubHeader() {
  const route = useLocation();
  const classes = useStyles();
  const uiService = useHtmlClassService();
  const subheader = useSubheader();
  const obsConfig = { attributes: true, childList: true, subtree: true };

  const layoutProps = useMemo(() => {
    return {
      config: uiService.config,
      subheaderMobileToggle: objectPath.get(
        uiService.config,
        'subheader.mobile-toggle'
      ),
      subheaderCssClasses: uiService.getClasses('subheader', true),
      subheaderContainerCssClasses: uiService.getClasses(
        'subheader_container',
        true
      ),
    };
  }, [uiService]);

  const fillBreadcrumbs = (aside, header) => {
    const breadcrumbs =
      aside?.aside?.breadcrumbs?.length > 0
        ? aside.aside.breadcrumbs
        : header.header.breadcrumbs;
    subheader.setBreadcrumbs(breadcrumbs);
    subheader.setTitle(
      aside?.aside && aside?.aside?.title && aside?.aside?.title?.length > 0
        ? aside.aside.title
        : header.header.title
    );
  };

  const aside = { aside: null };
  const header = { header: null };
  const obsCallback = useCallback(
    (mutationList) => {
      const menu = document.getElementById('kt_aside_menu');
      const activeLinksArray = Array.from(
        menu.getElementsByClassName('active') || []
      );
      aside.aside = getBreadcrumbsAndTitle('kt_aside_menu', route.pathname);
      header.header = getBreadcrumbsAndTitle('kt_header_menu', route.pathname);
      if (subheader.breadcrumbs?.length > 0) return;
      for (let i = 0; i < mutationList.length; i++) {
        if (mutationList[i].type === 'childList') {
          fillBreadcrumbs(aside, header);
          if (activeLinksArray.length > 0) return;
        }
      }
    },
    [subheader]
  );

  useEffect(() => {
    let obs;
    if (!subheader.breadcrumbs?.length) {
      obs = new MutationObserver(obsCallback);
      obs.observe(document.getElementById('kt_aside_menu'), obsConfig);
    } else {
      aside.aside = getBreadcrumbsAndTitle('kt_aside_menu', location.pathname);
      header.header = getBreadcrumbsAndTitle('kt_header', location.pathname);
      fillBreadcrumbs(aside, header);
    }

    return () => {
      obs?.disconnect();
    };
  }, [location.pathname, subheader.breadcrumbs.length]);

  // Do not remove this useEffect, need from update title/breadcrumbs outside (from the page)
  useEffect(() => {}, [subheader]);

  // ONLY SHOW DatePicker to the follwoing pages
  const showDatePicker =
    // All reporting menu except Live Queues
    (route.pathname.includes('/call-reporting/') &&
      !route.pathname.includes('/call-reporting/live-queues') &&
      !route.pathname.includes('/call-reporting/audit-trail') &&
      !route.pathname.includes('/internal/api-usage') &&
      !route.pathname.includes('/call-reporting/business-snapshot') &&
      !route.pathname.includes('/call-reporting/scheduled-reports')) ||
    // All History menu and submenues
    route.pathname.includes('/history/') ||
    // following three pages under myProfile and manage/Agents
    route.pathname.includes('/agent-reporting') ||
    route.pathname.includes('/call-history') ||
    route.pathname.includes('/sms-history') ||
    // Two reports under Cadences
    route.pathname.includes('/cadences/inflight-cadences') ||
    route.pathname.includes('/cadences/completed-actions') ||
    // Live Call Board
    route.pathname.includes('/call-board') ||
    route.pathname.includes('/internal/jira-issues') ||
    // Invoices table under billing tab
    route.pathname.includes('/billing/invoices') ||
    route.pathname.includes('/lead-flow');

  const userAtom = useRecoilValueLoadable(user);
  const loginUser = userAtom.contents?.user ?? {};
  const [agentUserIdAtom] = useRecoilStateLoadable(agentUserId);

  const includeBreadcrumbs =
    route.pathname.includes('/manage/leaderboards') ||
    route.pathname.includes('/call-reporting/live-queues');

  const pgDescText = document.getElementById('pgDescText')?.textContent ?? '';
  const bodyElement = document.getElementsByTagName('body')?.[0];
  useEffect(() => {
    if (pgDescText) {
      bodyElement.classList.toggle(
        'pgdesc-lg',
        pgDescText.length >= 160 && pgDescText.length <= 249
      );
      bodyElement.classList.toggle('pgdesc-xlg', pgDescText.length >= 250);
      bodyElement.classList.toggle(
        'pgdesc-sm',
        pgDescText.length <= 159 && pgDescText.length > 1
      );
      bodyElement.classList.remove('pgdesc-none');
    } else {
      bodyElement.classList.add('pgdesc-none');
      bodyElement.classList.remove('pgdesc-lg', 'pgdesc-xlg', 'pgdesc-sm');
    }
  }, [pgDescText]);

  return (
    <Paper
      square={true}
      elevation={0}
      id="kt_subheader"
      style={{ background: 'transparent' }}
    >
      <div
        id="subheader"
        className={`subheader py-2 py-lg-4 ${layoutProps.subheaderCssClasses} ${classes.subheader} ${classes.pgDesc}`}
      >
        <div
          className={`${layoutProps.subheaderContainerCssClasses} w-100 d-flex align-items-center justify-content-between flex-wrap flex-sm-nowrap`}
          style={{ minHeight: '51.5px' }}
        >
          {/* Info */}
          <div className="d-flex align-items-center flex-wrap mr-1">
            {layoutProps.subheaderMobileToggle && (
              <button
                className="burger-icon burger-icon-left mr-4 d-inline-block d-lg-none"
                id="kt_subheader_mobile_toggle"
              >
                <span />
              </button>
            )}

            <div className="d-flex align-items-baseline mr-5">
              <h5 className="font-weight-bold my-2 mr-5" id="pageTitle">
                <>
                  {window.location.pathname === '/manage/numbers/routing'
                    ? 'Inbound Routing'
                    : window.location.pathname ===
                      `/manage/agents/${agentUserIdAtom.contents.id}/edit`
                    ? 'Agent Settings'
                    : `${subheader.title}`}
                </>
              </h5>
            </div>
            {window.location.pathname === '/call-reporting/live-queues' ? (
              <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold p-0 my-2">
                <li className="breadcrumb-item">
                  <i className="flaticon2-shelter text-muted icon-1x"></i>
                </li>
                <li className="breadcrumb-item">
                  <span className="text-muted">Live</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="text-muted">Queues Live</span>
                </li>
              </ul>
            ) : null}
            {window.location.pathname === '/manage/leaderboards' ? (
              <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold p-0 my-2">
                <li className="breadcrumb-item">
                  <i className="flaticon2-shelter text-muted icon-1x"></i>
                </li>
                <li className="breadcrumb-item">
                  <span className="text-muted">Live</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="text-muted">Leaderboards</span>
                </li>
              </ul>
            ) : null}
            {!includeBreadcrumbs && (
              <BreadCrumbs items={subheader.breadcrumbs} />
            )}
          </div>

          {/* Toolbar */}
          <TimeGrouping />
          {showDatePicker && <DatePicker />}
        </div>
        <div className="pl-7">
          <div id="pgDescText" className={`pb-5 mb-0 ${classes.pgDescTxt}`}>
            {window.location.pathname === '/call-reporting/agent-reports'
              ? 'View a line chart of call metrics for your agents. Choose a Team, Metric and date range at the top of the page. Slice your data by Day, Week, or Month with Time Grouping at the top right.'
              : ''}
            {window.location.pathname === '/call-reporting/agent-status'
              ? 'View agent activity metrics like time spent idle, on call, or wrapping up. Compare these with call metrics like total calls handled to coach your agents. The line graph shows your selected metric and team, the table below shows all metrics. Use Search to filter and the CSV button to export.'
              : ''}
            {window.location.pathname === '/call-reporting/business-reports'
              ? 'View a line chart for Total Calls including outgoing and incoming for your agents. Charts include toggles for Answered, Missed, and Connected calls.'
              : ''}
            {window.location.pathname === '/call-reporting/business-snapshot'
              ? 'View call metrics for your business. Team and date range selections apply to all data. Call Metrics show selected date range compared to previous. Agent Metrics compare business totals vs individual agents.'
              : ''}
            {window.location.pathname === '/call-reporting/dispositions'
              ? 'View a count of Call Outcome uses per call per agent. Choose a team in the dropdown at the top of the page. Use Search to filter by email address.'
              : ''}
            {window.location.pathname === '/call-reporting/agent-summary'
              ? 'View and compare call metrics by agent on all teams or a specific team. The line graph shows your selected metric and team, the table below shows all metrics. Use Search to filter and the CSV button to export.'
              : ''}
            {window.location.pathname === '/call-reporting/inbound-summary'
              ? 'View a table of call stats for your Kixie Numbers allocated to inbound destinations. Search to filter by Number, Name, or Destination.'
              : ''}
            {window.location.pathname === '/call-reporting/sms-reports'
              ? 'View and compare SMS metrics by agent on all teams or a specific team. The line graph shows your selected metric and team, the table below shows all metrics. Use Search to filter and the CSV button to export.'
              : ''}
            {window.location.pathname === '/call-reporting/time-saved'
              ? 'View a line graph and table of Time Saved stats for your agents. Using Kixie features like click-to-call, SMS Templates, and Voicemail Drop save your agents’ time by automating common tasks.'
              : ''}
            {window.location.pathname === '/history/call-history'
              ? 'View details for incoming and outgoing calls for your business. See info like answered status, duration, recipient, and more. Access contact CRM details, listen to recordings, and send SMS from the Actions column.'
              : ''}
            {window.location.pathname === '/history/sms-history'
              ? 'View details for incoming and outgoing SMS messages for your business. Access contact CRM details and send SMS from the Actions column.'
              : ''}
            {window.location.pathname ===
            `/my-profile/${loginUser.userid}/agent-snapshot`
              ? "View call metrics for an agent. Call Metrics show selected date range compared to previous. Graph compares agent to all agents or a team's average. Call Review shows a selection of calls for the agent."
              : ''}
            {window.location.pathname ===
            `/my-profile/${loginUser.userid}/call-history`
              ? 'View details for incoming and outgoing calls for an agent. See info like answered status, duration, recipient, and more. Access contact CRM details, listen to recordings, and send SMS from the Actions column.'
              : ''}
            {window.location.pathname ===
            `/my-profile/${loginUser.userid}/devices-and-routing`
              ? 'View and configure agent call settings for incoming calls, call forwarding, and external devices. Click the pencil icon to edit and save your settings.'
              : ''}
            {window.location.pathname ===
              `/my-profile/${loginUser.userid}/edit` ||
            window.location.pathname ===
              `/manage/agents/${agentUserIdAtom.contents.id}/edit`
              ? 'View and configure stats, settings, and products for an agent. Click the pencil icon to edit and save settings like hours, voicemail options, caller ID and more.'
              : ''}
            {window.location.pathname ===
            `/my-profile/${loginUser.userid}/powerlists`
              ? 'View and configure PowerLists for an agent. Add, edit, and delete Personal PowerLists as well as view agent-subscribed Organizational PowerLists.'
              : ''}
            {window.location.pathname ===
            `/my-profile/${loginUser.userid}/numbers`
              ? 'View, add, and edit Kixie numbers for an agent. Click Add at the top right to search for and purchase a new number for this agent.'
              : ''}
            {window.location.pathname ===
            `/my-profile/${loginUser.userid}/agent-reporting`
              ? 'View Outgoing, Incoming, and Overall call metrics for an agent. Slice your data by Day, Week, or Month with Time Grouping at the top right.'
              : ''}
            {window.location.pathname ===
            `/my-profile/${loginUser.userid}/sms-history`
              ? 'View details for incoming and outgoing SMS messages for an agent. Access contact CRM details and send SMS from the Actions column.'
              : ''}
            {window.location.pathname ===
            `/my-profile/${loginUser.userid}/sms-template-help`
              ? 'View, add, and edit personalized SMS templates to quickly send prewritten messages. Use SMS Templates in Powercall or event-driven automations. Select Templates from the Send SMS window in Powercall.'
              : ''}
            {window.location.pathname ===
            `/my-profile/${loginUser.userid}/vm-drop`
              ? 'View, add, and edit personalized voicemail messages. Use VM Drop in Powercall  by clicking the icon at the top of the screen to leave a voicemail for an unanswered call without waiting for the beep.'
              : ''}
            {window.location.pathname === '/manage/account'
              ? 'View and configure stats, settings, and products for your business. Click the pencil icon to edit and save settings like business hours, call recording preferences, crm sync options, and much more.'
              : ''}
            {window.location.pathname === '/manage/agents'
              ? 'View, add, and edit all agents on your account. Use the Active toggle, column headers, and search bar to filter and arrange agents shown.'
              : ''}
            {window.location.pathname === '/call-reporting/audit-trail' &&
              'See a list of actions and changes made by users on this account.'}
            {window.location.pathname === '/manage/cadences' ? (
              <p>
                Cadences are event-driven call and SMS actions to facilitate
                speed-to-lead for your business. View, add, and configure your
                Cadences on this page. Go{' '}
                <a
                  href="https://support.kixie.com/hc/en-us/articles/4403830871579-Speed-to-Lead-Use-Case-for-Kixie-Cadences-"
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>{' '}
                to learn more.
              </p>
            ) : (
              ''
            )}
            {window.location.pathname === '/manage/cadences/completed-actions'
              ? 'View a list of all previously scheduled Cadence actions. See the time the action occurred, its name, target number, type, result, and retry attempts.'
              : ''}
            {window.location.pathname === '/manage/cadences/inflight-cadences'
              ? 'View a list of all upcoming scheduled Cadence actions and Cadences still processing. See the time the action was created, the scheduled time for the next action, and other details.'
              : ''}
            {window.location.pathname === '/manage/webhooks'
              ? 'View, add, and edit Webhooks. Click Add to create a new Webhook. Enter a name, provide a URL, select triggers, and configure other options.'
              : ''}
            {window.location.pathname === '/manage/dispositions'
              ? 'View, add, and edit call outcomes to categorize, label, and perform automations based on the outcome of your calls. Use outcomes to label calls, trigger Cadence automations, as well as categorize leads in your CRM.'
              : ''}
            {window.location.pathname === '/manage/conferences'
              ? 'View, add, and edit Conference calls on your account. Click Add to create the pencil icon to edit. Once a Conference is created, associate it with one of your numbers on the Kixie Numbers page.'
              : ''}
            {window.location.pathname === '/manage/numbers/routing'
              ? 'View, add, and edit Inbound Routing Rules for your Numbers. Dynamically route incoming calls to your business based on custom conditions. Rules run in the order they appear on this page and can be reordered.'
              : ''}
            {window.location.pathname === '/manage/ivrs'
              ? 'View, add, and edit IVR phone tree menus for your business. Customize greeting, hours, and options for forward calls to agents, queues, and more. Associate IVRs with numbers on the Kixie Numbers page.'
              : ''}
            {window.location.pathname === '/manage/queues' ? (
              <p>
                Queues place calls in line to be answered when agents busy on
                other calls become available. View, add, and configure Queues on
                this page. Setup by associating a Team and configuring options.
                Go{' '}
                <a
                  href="https://support.kixie.com/hc/en-us/articles/360047302494-Setting-Up-Your-Call-Queue"
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>{' '}
                to learn more.
              </p>
            ) : (
              ''
            )}
            {window.location.pathname === '/manage/ring-groups'
              ? 'Ring Groups allow calls to reach multiple agents on your business. View, add, and configure Ring Groups on this page. Customize settings like members, voicemail options, and more.'
              : ''}
            {window.location.pathname === '/manage/numbers/blocked'
              ? 'View, add, and edit Blocked Numbers on your account. Click Add to one or a group of numbers to block, then configure call and text settings for the number(s) entered.'
              : ''}
            {window.location.pathname === '/manage/numbers/unblocked'
              ? 'View, add, and edit phone numbers on your account. View and configure numbers associated with agents, queues, IVRs, and more. Click Add to search for and buy additional numbers.'
              : ''}
            {window.location.pathname === '/manage/numbers/business-sms'
              ? 'View, add, and edit Team SMS rules on your account. Click Add to create a new Team SMS rule. Select options like message time limits, business hours, auto replies, assigned agents and associated Kixie Numbers.'
              : ''}
            {window.location.pathname === '/manage/powerlists'
              ? 'Use PowerLists to multiline dial a list of numbers to reach leads faster and more efficiently. Add, edit, and configure PowerLists on this page. Upload lists, associate teams of agents, and view list statistics.'
              : ''}
            {window.location.pathname === '/manage/teams'
              ? 'Teams let you create groups of agents and are required for Kixie features like Queues, PowerLists, and Team SMS. View, add, and configure Teams on this page then associate them elsewhere on the dashboard.'
              : ''}
            {window.location.pathname === '/manage/leaderboards'
              ? 'View, add, and configure sales leaderboards and inbound queue slides on this page. Use these slides to display team stats on a TV or other screen. Use the preview and play buttons to view your slides.'
              : ''}
            {window.location.pathname === '/call-board'
              ? 'View real-time call status and metrics of agents on your account from this page. Blue is Online; Gray is Offline; Green is On Call; Red is Do-Not-Disturb (DND).'
              : ''}
            {window.location.pathname === '/lead-flow'
              ? 'Monitor incoming leads and their routing status through MaestroAI. Track lead progression from initial routing through agent contact, acceptance, connection, and outcome collection.'
              : ''}
            {window.location.pathname === '/call-reporting/live-queues'
              ? 'View real-time call status and metrics for your inbound queues. See the number of agents available as well as callers waiting on hold and waiting for callbacks. Click a Queue name to access and edit settings.'
              : ''}
            {window.location.pathname === '/internal/bid-search'
              ? 'Internal Use Only - View and export BID metrics'
              : ''}
          </div>
        </div>
      </div>
    </Paper>
  );
}
